import {
  commercialStatus,
  REPORT_GROUPS,
  type ReportGroup,
  reportEvents,
  reportLocation,
  reportsByGroup,
  type VoyageReportKind,
  vesselState,
} from '@felix/domain';
import { Button, Progress, Radio, Select } from '@mantine/core';
import {
  IconActivity,
  IconArrowRight,
  IconChartPie,
  IconClock,
  IconFilePlus,
  IconInfoCircle,
  IconRoute,
  IconShip,
  IconTimeline,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './AllReportsCards.module.css';
import { newReportKinds, quickCreatePath } from './all-reports.js';
import { useNumber } from './numbers.js';
import { useReportLabels } from './report-labels.js';
import type { Voyage, VoyageReport } from './types.js';
import { VOYAGE_TONE } from './VoyageFrame.js';
import { VoyageTimeline } from './VoyageTimeline.js';
import {
  activeVoyage,
  recentEvents,
  voyageProgress,
  voyageReportsOf,
  voyageSummaryOf,
} from './voyage-record.js';

const DAY_MS = 86_400_000;
const PERIODS = [30, 90, 365] as const;
const RECENT = 5;

export interface AllReportsCardsProps {
  scope: 'web' | 'ship';
  canCreate: boolean;
  vesselId: string | null;
  voyages: readonly Voyage[];
  reports: readonly VoyageReport[];
  onNavigate: (path: string) => void;
  /** Saat (testlerde sabit). */
  now?: () => Date;
}

/**
 * All Reports'un yan ve alt kartları (ADR 0082 Ek §4; ortağın `sefer-6.jpeg`): Quick Create Report
 * (yalnız gemide; mevcut rapor akışlarını açar), Vessel Status, Active Voyage, Recent Events,
 * Reports by Type ve etkin seferin Voyage Timeline'ı. Hepsi geminin raporlarından türetilir.
 */
export function AllReportsCards({
  scope,
  canCreate,
  vesselId,
  voyages,
  reports,
  onNavigate,
  now = () => new Date(),
}: AllReportsCardsProps) {
  const voyage = activeVoyage(voyages);
  const own = useMemo(() => (voyage ? voyageReportsOf(reports, voyage) : []), [reports, voyage]);
  const summary = useMemo(() => (voyage ? voyageSummaryOf(voyage, own) : null), [voyage, own]);
  const events = useMemo(() => reportEvents(reports), [reports]);
  const voyageEvents = useMemo(() => reportEvents(own), [own]);
  const vesselQuery = scope === 'web' && vesselId ? `&vessel=${vesselId}` : '';

  return (
    <>
      <div className={classes.grid} data-create={canCreate || undefined}>
        {canCreate ? (
          <QuickCreate voyages={voyages} onNavigate={onNavigate} className={classes.create} />
        ) : null}
        <VesselStatus reports={reports} events={events} className={classes.status} />
        <ActiveVoyage
          voyage={voyage}
          progress={voyage && summary ? voyageProgress(voyage, summary) : null}
          onDetails={() =>
            voyage && onNavigate(`/voyage/details?voyage=${voyage.id}${vesselQuery}`)
          }
          className={classes.active}
        />
        <RecentEvents
          reports={reports}
          events={events}
          onAll={() =>
            onNavigate(
              voyage ? `/voyage/timeline?voyage=${voyage.id}${vesselQuery}` : '/voyage/timeline',
            )
          }
          className={classes.events}
        />
        <ReportsByType reports={reports} now={now} className={classes.types} />
      </div>
      {voyage && summary ? (
        <VoyageTimeline
          summary={summary}
          events={voyageEvents}
          reports={own}
          eta={
            voyage.status === 'completed' ? null : { at: voyage.plannedEta, port: voyage.toPort }
          }
        />
      ) : null}
    </>
  );
}

function QuickCreate({
  voyages,
  onNavigate,
  className,
}: {
  voyages: readonly Voyage[];
  onNavigate: (path: string) => void;
  className: string | undefined;
}) {
  const { t } = useTranslation();
  const labels = useReportLabels();
  const k = (key: string) => t(`voyage.record.cards.${key}`);
  const writable = voyages.filter((v) => v.status === 'active' || v.status === 'planned');
  const [kind, setKind] = useState<VoyageReportKind>('departure');
  const [association, setAssociation] = useState<'none' | 'link' | 'new'>('none');
  const [voyageId, setVoyageId] = useState<string | null>(activeVoyage(voyages)?.id ?? null);
  // Her tür sefersiz girilir (ADR 0083 §1); sıra geminin zaman çizgisinde denetlenir
  const blocked = association === 'link' && !writable.some((v) => v.id === voyageId);
  const create = () => onNavigate(quickCreatePath(kind, association, voyageId));
  const hint =
    association === 'none'
      ? k('noVoyageHint')
      : association === 'link'
        ? k('linkHint')
        : k('newHint');
  return (
    <Panel
      title={k('quickCreate')}
      icon={<IconFilePlus size={20} stroke={1.8} />}
      className={className}
    >
      <div className={classes.form}>
        <Select
          label={k('reportType')}
          data={newReportKinds(null).map((group) => ({
            group: t(`voyage.allReports.types.${group.type}`),
            items: group.kinds.map((value) => ({
              value,
              label: labels.typeLabel(value, group.type),
            })),
          }))}
          value={kind}
          allowDeselect={false}
          onChange={(value) => value && setKind(value as VoyageReportKind)}
        />
        <Radio.Group
          label={k('association')}
          value={association}
          onChange={(value) => setAssociation(value as typeof association)}
        >
          <div className={classes.radios}>
            <Radio value="none" label={k('noVoyage')} />
            <Radio value="link" label={k('linkVoyage')} />
            <Radio value="new" label={k('newVoyage')} />
          </div>
        </Radio.Group>
        {association === 'link' ? (
          <Select
            label={k('selectVoyage')}
            data={writable.map((v) => ({ value: v.id, label: v.voyageNo }))}
            value={voyageId}
            placeholder={k('noWritable')}
            allowDeselect={false}
            disabled={writable.length === 0}
            onChange={setVoyageId}
          />
        ) : null}
        <p className={classes.hint}>
          <IconInfoCircle size={14} aria-hidden />
          <span>{hint}</span>
        </p>
        <Button
          fullWidth
          rightSection={<IconArrowRight size={16} />}
          disabled={blocked}
          onClick={create}
        >
          {k('create')}
        </Button>
      </div>
    </Panel>
  );
}

function VesselStatus({
  reports,
  events,
  className,
}: {
  reports: readonly VoyageReport[];
  events: ReturnType<typeof reportEvents>;
  className: string | undefined;
}) {
  const { t } = useTranslation();
  const labels = useReportLabels();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.record.cards.${key}`, options);
  const state = vesselState(reports);
  const last = [...reports]
    .filter((r) => r.status === 'submitted')
    .sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt))
    .at(-1);
  const lastEvent = events.at(-1);
  const tiles = [
    {
      key: 'navigation',
      icon: <IconShip size={18} />,
      value: t(`voyage.record.navigation.${state ?? 'unknown'}`),
      label: k('navigationStatus'),
      tone: state ? state : 'unknown',
    },
    {
      key: 'commercial',
      icon: <IconActivity size={18} />,
      value: t(`voyage.record.commercial.${commercialStatus(reports)}`),
      label: k('commercialStatus'),
      tone: 'neutral',
    },
    {
      key: 'report',
      icon: <IconClock size={18} />,
      value: last ? formatDateTime(last.reportedAt, 0) : '—',
      label: last ? k('lastReport', { type: labels.typeLabel(last.kind) }) : k('lastReportNone'),
      tone: 'neutral',
    },
    {
      key: 'event',
      icon: <IconClock size={18} />,
      value: lastEvent ? formatDateTime(lastEvent.at, 0) : '—',
      label: lastEvent
        ? k('lastEvent', { type: t(`voyage.record.eventTypes.${lastEvent.type}`) })
        : k('lastEventNone'),
      tone: 'neutral',
    },
  ];
  return (
    <Panel
      title={k('vesselStatus')}
      icon={<IconShip size={20} stroke={1.8} />}
      className={className}
    >
      <ul className={classes.tiles}>
        {tiles.map((tile) => (
          <li key={tile.key} className={classes.tile} data-tone={tile.tone}>
            <span className={classes.tileIcon} aria-hidden>
              {tile.icon}
            </span>
            <span className={classes.tileText}>
              <strong>{tile.value}</strong>
              <span>{tile.label}</span>
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function ActiveVoyage({
  voyage,
  progress,
  onDetails,
  className,
}: {
  voyage: Voyage | null;
  progress: ReturnType<typeof voyageProgress> | null;
  onDetails: () => void;
  className: string | undefined;
}) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.record.cards.${key}`, options);
  return (
    <Panel
      title={k('activeVoyage')}
      icon={<IconRoute size={20} stroke={1.8} />}
      extra={
        voyage ? (
          <Button size="xs" variant="default" onClick={onDetails}>
            {k('viewDetails')}
          </Button>
        ) : null
      }
      className={className}
    >
      {voyage && progress ? (
        <div className={classes.voyage}>
          <div className={classes.voyageMain}>
            <div className={classes.voyageHead}>
              <strong className={classes.voyageNo}>{voyage.voyageNo}</strong>
              <StatusBadge tone={VOYAGE_TONE[voyage.status]}>
                {t(`voyage.voyageStatus.${voyage.status}`)}
              </StatusBadge>
            </div>
            <p className={classes.route}>
              <span>{voyage.fromPort}</span>
              <IconArrowRight size={16} aria-hidden />
              <span>{voyage.toPort}</span>
            </p>
            <Progress
              value={progress.completedPct ?? 0}
              size="md"
              radius="xl"
              aria-label={k('completed')}
            />
            <p className={classes.progressText}>
              <span>{progress.legNo ? k('legOf', { no: progress.legNo }) : '—'}</span>
              <span>
                {progress.remainingNm === null
                  ? '—'
                  : k('remaining', { value: n.unit(progress.remainingNm, 'nm', 0) })}
              </span>
            </p>
          </div>
          <dl className={classes.facts}>
            <dt>{k('etd')}</dt>
            <dd>{formatDateTime(voyage.start ?? voyage.plannedEtd ?? null, 0)}</dd>
            <dt>{k('eta')}</dt>
            <dd>
              {formatDateTime(voyage.plannedEta, 0)} {voyage.status !== 'completed' ? k('est') : ''}
            </dd>
            <dt>{k('distanceTotal')}</dt>
            <dd>{n.unit(progress.plannedNm || null, 'nm', 0)}</dd>
            <dt>{k('completed')}</dt>
            <dd>
              {n.unit(progress.actualNm, 'nm', 0)}
              {progress.completedPct === null ? '' : ` (${progress.completedPct}%)`}
            </dd>
          </dl>
        </div>
      ) : (
        <EmptyState title={k('noActive')} description={k('noActiveNote')} />
      )}
    </Panel>
  );
}

function RecentEvents({
  reports,
  events,
  onAll,
  className,
}: {
  reports: readonly VoyageReport[];
  events: ReturnType<typeof reportEvents>;
  onAll: () => void;
  className: string | undefined;
}) {
  const { t } = useTranslation();
  const labels = useReportLabels();
  const k = (key: string) => t(`voyage.record.cards.${key}`);
  const byId = useMemo(() => new Map(reports.map((r) => [r.id, r])), [reports]);
  const recent = recentEvents(events, RECENT);
  return (
    <Panel
      title={k('recentEvents')}
      icon={<IconTimeline size={20} stroke={1.8} />}
      extra={
        <Button size="xs" variant="default" onClick={onAll}>
          {k('viewAll')}
        </Button>
      }
      className={className}
    >
      {recent.length === 0 ? (
        <p className={classes.empty}>{k('noEvents')}</p>
      ) : (
        <table className={classes.events}>
          <caption className={classes.srOnly}>{k('recentEvents')}</caption>
          <thead className={classes.srOnly}>
            <tr>
              <th scope="col">{t('voyage.record.columns.date')}</th>
              <th scope="col">{t('voyage.record.columns.event')}</th>
              <th scope="col">{t('voyage.record.columns.location')}</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((event) => {
              const report = byId.get(event.reportId);
              const location = report ? labels.locationLabel(reportLocation(report)) : '—';
              return (
                <tr key={event.key}>
                  <td>
                    <span className={classes.eventDot} data-category={event.category} aria-hidden />
                    {formatDateTime(event.at, 0)}
                  </td>
                  <td className={classes.eventName}>
                    {t(`voyage.record.eventTypes.${event.type}`)}
                  </td>
                  <td className={classes.ellipsis} title={location}>
                    {location}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

const GROUP_COLOR: Record<ReportGroup, string> = {
  noon: 'var(--felix-primary-600)',
  port: 'var(--felix-activity-anchor)',
  arrival_departure: 'var(--felix-danger-fg)',
  anchorage_drift: 'var(--felix-activity-port)',
  other: 'var(--felix-activity-rest)',
};

function ReportsByType({
  reports,
  now,
  className,
}: {
  reports: readonly VoyageReport[];
  now: () => Date;
  className: string | undefined;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.record.cards.${key}`, options);
  const [days, setDays] = useState<(typeof PERIODS)[number]>(90);
  const since = new Date(now().getTime() - days * DAY_MS).toISOString();
  const counts = reportsByGroup(reports, since);
  const total = counts.reduce((sum, row) => sum + row.count, 0);
  // Halka: çevre 100 birim (r ≈ 15.915); dilimler sırayla
  let offset = 0;
  const slices = counts
    .filter((row) => row.count > 0)
    .map((row) => {
      const length = (row.count / total) * 100;
      const slice = { group: row.group, length, offset };
      offset += length;
      return slice;
    });
  return (
    <Panel
      title={k('byType')}
      icon={<IconChartPie size={20} stroke={1.8} />}
      extra={
        <Select
          size="xs"
          aria-label={k('period')}
          className={classes.period}
          data={PERIODS.map((value) => ({
            value: String(value),
            label: k('lastDays', { count: value }),
          }))}
          value={String(days)}
          allowDeselect={false}
          onChange={(value) => value && setDays(Number(value) as (typeof PERIODS)[number])}
        />
      }
      className={className}
    >
      <div className={classes.byType}>
        <figure className={classes.donut} aria-label={k('chart')}>
          <svg viewBox="0 0 42 42" aria-hidden>
            <circle cx="21" cy="21" r="15.915" className={classes.ring} />
            {slices.map((slice) => (
              <circle
                key={slice.group}
                cx="21"
                cy="21"
                r="15.915"
                fill="none"
                stroke={GROUP_COLOR[slice.group]}
                strokeWidth="6"
                strokeDasharray={`${slice.length} ${100 - slice.length}`}
                strokeDashoffset={25 - slice.offset}
              />
            ))}
          </svg>
          <figcaption className={classes.donutText}>
            <strong className={classes.donutCount}>{total}</strong>
            <span>{k('reports')}</span>
          </figcaption>
        </figure>
        <ul className={classes.legend}>
          {REPORT_GROUPS.map((group) => {
            const count = counts.find((row) => row.group === group)?.count ?? 0;
            return (
              <li key={group}>
                <span
                  className={classes.legendDot}
                  style={{ background: GROUP_COLOR[group] }}
                  aria-hidden
                />
                <span className={classes.legendName} title={k(`groups.${group}`)}>
                  {k(`groups.${group}`)}
                </span>
                <span className={classes.legendCount}>{count}</span>
                <span className={classes.legendPct}>
                  ({total > 0 ? Math.round((count / total) * 100) : 0}%)
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </Panel>
  );
}
